package com.xtream.ui.tv.presentation.playlist

// Import official Tv Material elements
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.calculateStartPadding
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.PlaylistAdd
import androidx.compose.material.icons.outlined.Refresh
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.xtream.composeapp.LocalAppViewModelStoreOwner
import com.xtream.composeapp.SharedBuildKonfig
import com.xtream.composeapp.data.Screen
import com.xtream.composeapp.domain.sealed.ApiSealed
import com.xtream.composeapp.platform.getDeviceInfo
import com.xtream.composeapp.presentation.ui.navigation.RootNavViewModel
import com.xtream.composeapp.viewmodel.AppSettingsViewModel
import com.xtream.composeapp.viewmodel.PlayerServerViewModel
import com.xtream.composeapp.viewmodel.UserViewModel
import com.xtream.data_kmp.dto.PlayerPlaylistItem
import com.xtream.sharedResources.resources.Res
import com.xtream.sharedResources.resources.playlists
import com.xtream.sharedResources.resources.qr_code
import com.xtream.sharedResources.resources.refresh
import com.xtream.ui.ui.widget.TvButton
import com.xtream.ui.ui.widget.rememberPlainQrCodePainter
import io.ktor.http.Url
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kotlin.math.absoluteValue


@Composable
fun PlaylistScreen(
    playerViewModel: PlayerServerViewModel = koinViewModel(),
    userViewModel: UserViewModel = koinViewModel(),
    appSettingsViewModel: AppSettingsViewModel = koinViewModel(),
    contentPadding: PaddingValues = PaddingValues(vertical = 24.dp),
) {
    val viewModelStoreOwner = LocalAppViewModelStoreOwner.current
    val rootNavViewModel: RootNavViewModel = koinViewModel(
        viewModelStoreOwner = viewModelStoreOwner ?: error("no viewModelStoreOwner provided")
    )
    val state = playerViewModel.playlistState
    val deviceInfo = getDeviceInfo()

    fun login(item: PlayerPlaylistItem) {
        val url = Url(item.url ?: "")
        val username = url.parameters["username"] ?: ""
        val password = url.parameters["password"] ?: ""
        val baseUrl = "${url.protocol.name}://${url.host}" +
                if (url.port != url.protocol.defaultPort) ":${url.port}" else ""
        println("Url: $baseUrl")
        userViewModel.loadUser(
            username = username,
            password = password,
            serverUrl = baseUrl,
            onSuccess = {
                appSettingsViewModel.setLastUpdatedDataMillis(null) {
                    appSettingsViewModel.setSkipAccountRefresh(true) {
                        rootNavViewModel.backStack.clear()
                        rootNavViewModel.backStack.add(
                            Screen.Loading(forceRefreshData = true, skipUserRefresh = true)
                        )
                    }
                }
            }
        )
    }

    fun loadData(){
        playerViewModel.loadPlaylist(deviceInfo = deviceInfo)
        playerViewModel.checkStatus(deviceInfo = deviceInfo)}

    val statusState = playerViewModel.deviceStatusState


    LaunchedEffect(Unit) {
        playerViewModel.registerDevice(deviceInfo = deviceInfo)
        loadData()

    }

    val layoutDirection = LocalLayoutDirection.current

    Row(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
            .padding(
                PaddingValues(
                    start = contentPadding.calculateStartPadding(layoutDirection),
                    end = 48.dp,
                    bottom = 48.dp
                )
            ),
        horizontalArrangement = Arrangement.spacedBy(32.dp)
    ) {
        // ---------------- LEFT COLUMN: PLAYLIST SELECTION ----------------
        Column(
            modifier = Modifier
                .weight(0.6f)
                .fillMaxHeight()
                .padding(24.dp),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {

                    Text(
                        text = stringResource(Res.string.playlists),
                        style = MaterialTheme.typography.headlineSmall,
                        color = MaterialTheme.colorScheme.onSurface,
                        fontWeight = FontWeight.Bold
                    )
                Spacer(Modifier.weight(1f))

                IconButton(
                    onClick = { }
                ) {
                    Icon(
                        imageVector = Icons.AutoMirrored.Default.PlaylistAdd,
                        contentDescription = "add playlist",
                        tint = MaterialTheme.colorScheme.onSurface
                    )
                }
                IconButton(
                    onClick = {  playerViewModel.loadPlaylist(deviceInfo = deviceInfo)  }
                ) {
                    Icon(
                        imageVector = Icons.Outlined.Refresh,
                        contentDescription = stringResource(Res.string.refresh),
                        tint = MaterialTheme.colorScheme.onSurface
                    )
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            when (state) {
                is ApiSealed.Initial -> {}

                is ApiSealed.Loading -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                    }
                }

                is ApiSealed.Error -> {
                    Text(
                        text = state.message,
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.error
                    )
                }

                is ApiSealed.Success  -> {
                    val items = state.data.playlists
                    val avatarColors = listOf(
                        MaterialTheme.colorScheme.primaryContainer to MaterialTheme.colorScheme.onPrimaryContainer,
                        MaterialTheme.colorScheme.secondaryContainer to MaterialTheme.colorScheme.onSecondaryContainer,
                        MaterialTheme.colorScheme.tertiaryContainer to MaterialTheme.colorScheme.onTertiaryContainer,
                    )

                    LazyColumn (

                        modifier = Modifier.fillMaxSize()
                    ) {
                        items(items) { item ->
                            val initials = (item.name ?: "?")
                                .split(" ")
                                .take(2)
                                .joinToString("") { it.take(1).uppercase() }
                            val (bgColor, textColor) = avatarColors[
                                (item.name ?: "").hashCode().absoluteValue % avatarColors.size
                            ]

                            TvButton(
                                title = item.name ?: "Unknown",
                                fillParent = true,
                                //subtitle = item.category ?: "No Category",
                                modifier = Modifier.fillMaxWidth(),
                                onClick = { login(item) },
                                startContent = {
                                    Box(
                                        modifier = Modifier
                                            .size(36.dp)
                                            .clip(CircleShape)
                                            .background(bgColor),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text(
                                            text = initials,
                                            style = MaterialTheme.typography.labelMedium,
                                            fontWeight = FontWeight.Medium,
                                            color = textColor
                                        )
                                    }
                                }
                            )
                        }
                    }
                }

                else -> {}
            }
        }

        // ---------------- RIGHT COLUMN: DEVICE INFO & QR ----------------
        Column(
            modifier = Modifier
                .weight(0.4f)
                .fillMaxHeight()
                .clip(RoundedCornerShape(16.dp))
                .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f))
                .padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Text(
                text = "Device Info",
                style = MaterialTheme.typography.headlineSmall,
                color = MaterialTheme.colorScheme.onSurface,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.align(Alignment.Start)
            )

            Image(
                modifier = Modifier
                    .size(180.dp)
                    .aspectRatio(1f)
                    .clip(RoundedCornerShape(12.dp))
                    .background(Color.Black)
                    .padding(16.dp),
                painter = rememberPlainQrCodePainter(SharedBuildKonfig.QR_CODE_URL.plus(deviceInfo.macAddress.uppercase())),
                contentDescription = stringResource(Res.string.qr_code)
            )

            when (statusState) {
                is ApiSealed.Loading -> {

                }
                is ApiSealed.Success -> {
                    val body = statusState.data
                    val isActivated = body.isActivated
                    val activation = body.activation

                    FlowRow(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                        maxItemsInEachRow = 2
                    ) {
                        DeviceInfoCard(
                            title = "MAC Address",
                            value = deviceInfo.macAddress.uppercase(),
                            modifier = Modifier.weight(1f)
                        )
                        DeviceInfoCard(
                            title = "Device Key",
                            value = deviceInfo.deviceId.substring(0, 6),
                            modifier = Modifier.weight(1f)
                        )
                        DeviceInfoCard(
                            title = "Status",
                            value = if (isActivated)
                                activation?.status?.replaceFirstChar { it.uppercase() } ?: "Active"
                            else "Not Activated",
                            modifier = Modifier.weight(1f)
                        )
                        DeviceInfoCard(
                            title = "Package",
                            value = activation?.packageName ?: "—",
                            modifier = Modifier.weight(1f)
                        )
                        DeviceInfoCard(
                            title = "Expires At",
                            value = activation?.expiresAt?.take(10) ?: "—",
                            modifier = Modifier.weight(1f)
                        )
                        DeviceInfoCard(
                            title = "Registered",
                            value = "—",
                            modifier = Modifier.weight(1f)
                        )
                    }
                }
                is ApiSealed.Error -> {
                    Text(
                        text = statusState.message,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.error
                    )
                }

                else -> {}
            }
        }
    }
}

@Composable
fun DeviceInfoCard(
    title: String,
    value: String,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(MaterialTheme.colorScheme.surface)
            .padding(12.dp)
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )

        Spacer(modifier = Modifier.height(4.dp))

        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurface,
            fontWeight = FontWeight.Medium,
            maxLines = 1
        )
    }
}